import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { FormGroup, FormLabel, Grid, Stack, Tooltip, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { useSelector } from 'react-redux';
import FolioRadioGroup from '@/components/FolioRadioGroup';
import { useClientSchemaContext } from '@/context/clientSchemaContext';
import { getGridLayout } from '@/utils/fieldLayout';
import { isProspectFieldVisible } from '@/features/config/helper';

const GROUP = 'clientStatus';

const ClientStatusField = ({ field, disabled }) => {
	const theme = useTheme();
	const { values, setFieldValue, touched } = useFormikContext();
	const { setIsProspect } = useClientSchemaContext();

	const options = useMemo(() => {
		const data = (field.field?.options ?? []).slice();
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [field.field?.options]);

	const isProspectField = field.name === 'ClientTypeId';
	const fieldDisabled =
		isProspectField &&
		values['existingClient'] &&
		values['existingClient']?.insight?.ClientTypeId === 0;

	const helperText = field.helperText;

	const currentValue = options.find((v) => v.value === values[field.name])?.value ?? '';

	return (
		<>
			<Stack direction='row' justifyContent='space-between' alignItems='center'>
				<FormLabel htmlFor={field.name} disabled={disabled}>
					{field.label}
				</FormLabel>
				{helperText && (
					<Tooltip title={helperText}>
						<InfoOutlineIcon fontSize='1.25rem' sx={{ color: theme.palette.text.secondary }} />
					</Tooltip>
				)}
			</Stack>
			<FolioRadioGroup
				name={field.name}
				value={currentValue}
				onChange={(e) => {
					const newValue = Number(e.target.value);
					console.log(field.name, e.target.value, typeof e.target.value);
					setFieldValue(field.name, newValue);
					if (isProspectField) {
						const currentValues = Object.entries(values).filter(([k]) => touched[k]);
						setIsProspect(newValue === 1, 3000);
						setTimeout(() => {
							currentValues.forEach(([k, v]) => {
								if (v != null) {
									setFieldValue(k, v);
								}
							});
						}, 3000);
					}
				}}
				disabled={disabled || fieldDisabled}
				items={options}
			/>
		</>
	);
};
const ClientStatusFields = ({ disabled }) => {
	const { view, fieldDensity, isProspect } = useClientSchemaContext();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter(
						(f) =>
							f.group === GROUP &&
							(isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view]),
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		const data = (fieldsQuery.data ?? []).slice();
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldsQuery.data]);

	const sizeSchema = useMemo(() => getGridLayout(fields, fieldDensity), [fields, fieldDensity]);

	const getField = useCallback(
		(field) => {
			const size = sizeSchema[field.name];
			return (
				<Grid {...{ size }} key={field.name}>
					<ClientStatusField {...{ field, disabled }} />
				</Grid>
			);
		},
		[fields, disabled, sizeSchema],
	);

	if (fields.length === 0) return <></>;

	return (
		<FormGroup width='100%'>
			<Grid container rowSpacing={1} columnSpacing={2}>
				{fields.map(getField)}
			</Grid>
		</FormGroup>
	);
};
export default ClientStatusFields;
